import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiMail, FiAlertCircle, FiShield, FiCheckCircle, FiActivity,
  FiTrendingUp, FiZap, FiKey, FiUsers, FiBox, FiRefreshCw
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const KPI = ({ label, value, icon, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600'
  };
  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <SafeIcon icon={icon} />
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-4">{value}</p>
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-1">{label}</p>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await gatewayApi.dashboardSummary();
      setSummary(response.data);
    } catch (err) {
      setError(err.message || 'Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = summary?.counts || {};
  const activeProvider = useMemo(
    () => summary?.providers?.find(provider => provider.mode === 'Primary') || summary?.providers?.[0],
    [summary]
  );

  const quotaWatch = useMemo(() => {
    if (!summary?.clients) return [];
    return summary.clients
      .map(client => {
        const limit = Number(client.monthlyLimit || 0);
        const usage = 0;
        return {
          ...client,
          usage,
          limit,
          percent: limit ? Math.min(100, (usage / limit) * 100) : 0
        };
      })
      .slice(0, 8);
  }, [summary]);

  const expiringKeys = useMemo(() => {
    const today = new Date();
    const threshold = new Date(today);
    threshold.setDate(threshold.getDate() + 30);
    return (summary?.apiKeys || []).filter(key => {
      if (!key.validUntil || key.status === 'revoked') return false;
      const expiry = new Date(`${key.validUntil}T23:59:59`);
      return expiry <= threshold;
    });
  }, [summary]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Gateway Operations</h2>
          <p className="text-sm text-slate-500">Live operational data from PocketBase and the BPA Gateway.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/connect-app")} className="px-4 py-2.5 bg-blue-600 rounded-xl text-sm font-bold text-white flex items-center gap-2 hover:bg-blue-700"><SafeIcon icon={FiZap}/> Connect an app</button>
        <button onClick={load} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-lg border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <SafeIcon icon={FiActivity} className="text-emerald-400 text-xl" />
          </div>
          <div>
            <h3 className="font-bold">Production Control Plane</h3>
            <p className="text-xs text-slate-400 mt-1">
              Storage: PocketBase • Provider: {activeProvider?.name || 'Emailit'} • Mode: {activeProvider?.mode || 'Primary'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs font-bold">
          <span className="px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
            <SafeIcon icon={FiCheckCircle} className="inline mr-1" /> Live
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPI label="Clients" value={loading ? '…' : counts.clients || 0} icon={FiUsers} />
        <KPI label="Active Apps" value={loading ? '…' : counts.activeApps || 0} icon={FiBox} />
        <KPI label="Active Keys" value={loading ? '…' : counts.activeKeys || 0} icon={FiKey} tone="green" />
        <KPI label="Sent Today" value={loading ? '…' : Number(counts.sentToday || 0).toLocaleString()} icon={FiMail} tone="green" />
        <KPI label="Sent This Month" value={loading ? '…' : Number(counts.sentMonth || 0).toLocaleString()} icon={FiTrendingUp} />
        <KPI label="Failed Today" value={loading ? '…' : Number(counts.failedToday || 0).toLocaleString()} icon={FiAlertCircle} tone={counts.failedToday ? 'red' : 'green'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-slate-900">Client Control Status</h3>
              <p className="text-xs text-slate-500">Current client lifecycle and configured limits.</p>
            </div>
            <SafeIcon icon={FiZap} className="text-blue-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
                  <th className="px-5 py-3">Client</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Monthly Limit</th>
                  <th className="px-5 py-3">Included Allowance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {quotaWatch.map(client => (
                  <tr key={client.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4 font-bold text-slate-800">{client.name}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${client.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{Number(client.monthlyLimit || 0).toLocaleString()}</td>
                    <td className="px-5 py-4 text-slate-600">{Number(client.includedEmailLimit || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {!loading && quotaWatch.length === 0 && (
                  <tr><td colSpan="4" className="px-5 py-10 text-center text-slate-400">No clients yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-slate-900">Security & Key Alerts</h3>
              <p className="text-xs text-slate-500">Expiring or lifecycle-sensitive credentials.</p>
            </div>
            <SafeIcon icon={FiShield} className="text-slate-400" />
          </div>
          <div className="p-4 space-y-3">
            {expiringKeys.length ? expiringKeys.map(key => (
              <button key={key.id} onClick={() => navigate('/api-keys')} className="w-full text-left p-3 bg-amber-50 border border-amber-100 rounded-xl hover:border-amber-300">
                <p className="text-xs font-bold text-slate-800">{key.name}</p>
                <p className="text-[10px] text-slate-500 mt-1">{key.maskedKey}</p>
                <p className="text-[10px] font-bold text-amber-700 mt-2">Valid until {key.validUntil}</p>
              </button>
            )) : (
              <div className="p-5 bg-emerald-50 border border-emerald-100 rounded-xl text-center">
                <SafeIcon icon={FiCheckCircle} className="text-emerald-600 mx-auto text-xl" />
                <p className="text-xs font-bold text-emerald-800 mt-2">No key expiry alerts</p>
              </div>
            )}

            <button onClick={() => navigate('/api-keys')} className="w-full py-2.5 text-xs font-bold text-blue-600 hover:bg-blue-50 rounded-lg">
              Manage API Keys
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <h3 className="font-bold text-slate-900">Phase 2 Enforcement</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <Status label="Origin Validation" />
          <Status label="Quota Enforcement" />
          <Status label="Recipient Policy" />
          <Status label="Silent Honeypot" />
        </div>
      </div>
    </div>
  );
};

const Status = ({ label }) => (
  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-xs font-bold text-emerald-800">
    <SafeIcon icon={FiCheckCircle} className="text-emerald-600" /> {label}
  </div>
);

export default Dashboard;